import { FarmCostGroup } from './Farm'
import { ProductMeasureKind } from './Product'
import { FarmJobFamily, FarmJobTypeCategory } from './WorkReport'

/** A platform operator. Not a client user: it has no client, tenants or client permissions. */
export type PlatformAdmin = {
  id: number
  email: string
  firstName: string
  lastName: string
}

/** The catalogs shared by every client. Values match the API enum and are used in the URL. */
export enum GlobalCatalog {
  FarmJobType = 'FarmJobType',
  FarmProductType = 'FarmProductType',
  FarmExpenseType = 'FarmExpenseType',
  ProductInventoryCategory = 'ProductInventoryCategory',
  ProductMeasure = 'ProductMeasure',
  Bank = 'Bank',
}

export type GlobalCatalogField = 'description' | 'category' | 'family' | 'costGroup' | 'kind'

export type GlobalCatalogDefinition = {
  catalog: GlobalCatalog
  /** URL segment under /plataforma/catalogos. */
  slug: string
  title: string
  singular: string
  /** Each client picks which rows it uses (bridge table) instead of seeing all of them. */
  isAssociable: boolean
  fields: GlobalCatalogField[]
}

export const globalCatalogs: GlobalCatalogDefinition[] = [
  { catalog: GlobalCatalog.FarmJobType, slug: 'trabajos-fincas', title: 'Trabajos finca', singular: 'trabajo', isAssociable: true, fields: ['category', 'family'] },
  { catalog: GlobalCatalog.FarmProductType, slug: 'productos-fincas', title: 'Productos finca', singular: 'producto', isAssociable: true, fields: [] },
  { catalog: GlobalCatalog.FarmExpenseType, slug: 'tipos-gastos', title: 'Tipos de gasto', singular: 'tipo de gasto', isAssociable: true, fields: ['description', 'costGroup'] },
  { catalog: GlobalCatalog.ProductInventoryCategory, slug: 'categorias-inventario', title: 'Categorías de inventario', singular: 'categoría', isAssociable: false, fields: [] },
  { catalog: GlobalCatalog.ProductMeasure, slug: 'unidades-medida', title: 'Unidades de medida', singular: 'unidad', isAssociable: false, fields: ['kind'] },
  { catalog: GlobalCatalog.Bank, slug: 'bancos', title: 'Bancos', singular: 'banco', isAssociable: false, fields: [] },
]

export const findGlobalCatalogBySlug = (slug?: string) =>
  globalCatalogs.find((definition) => definition.slug === slug)

export type GlobalCatalogItem = {
  id: number
  name: string
  description?: string | null
  category?: FarmJobTypeCategory | null
  family?: FarmJobFamily | null
  costGroup?: FarmCostGroup | null
  kind?: ProductMeasureKind | null
  isActive: boolean
  clientCount?: number | null
  referenceCount: number
}

export type GlobalCatalogItemRequest = {
  name: string
  description?: string | null
  category?: FarmJobTypeCategory | null
  family?: FarmJobFamily | null
  costGroup?: FarmCostGroup | null
  kind?: ProductMeasureKind | null
}

export type PlatformClient = {
  id: number
  name: string
  description: string
  isActive: boolean
  createdDate: string
}

export type PlatformClientContact = {
  id: number
  clientId: number
  firstName: string
  lastName: string
  phoneNumber?: string | null
  email: string
  role: string
  address: string
}

export type PlatformTenant = {
  id: number
  clientId: number
  name: string
  isDeleted: boolean
}

export enum PlatformAuditAction {
  Create = 1,
  Edit = 2,
  Activate = 3,
  Deactivate = 4,
  Delete = 5,
  Associate = 6,
  Suspend = 7,
  Reactivate = 8,
}

export function getPlatformAuditActionLabel(action: PlatformAuditAction): string {
  switch (action) {
    case PlatformAuditAction.Create:
      return 'Creación'
    case PlatformAuditAction.Edit:
      return 'Edición'
    case PlatformAuditAction.Activate:
      return 'Activación'
    case PlatformAuditAction.Deactivate:
      return 'Desactivación'
    case PlatformAuditAction.Delete:
      return 'Eliminación'
    case PlatformAuditAction.Associate:
      return 'Asociación'
    case PlatformAuditAction.Suspend:
      return 'Suspensión'
    case PlatformAuditAction.Reactivate:
      return 'Reactivación'
    default:
      return 'Desconocido'
  }
}

const auditEntityLabels: Record<string, string> = {
  Client: 'Cliente',
  ClientContact: 'Contacto',
  Tenant: 'Perfil',
  User: 'Administrador',
  ...Object.fromEntries(globalCatalogs.map((definition) => [definition.catalog, definition.title])),
}

export const getAuditEntityLabel = (entityName: string) => auditEntityLabels[entityName] ?? entityName

export type PlatformAuditLog = {
  id: number
  platformAdminId: number
  platformAdminEmail: string
  action: PlatformAuditAction
  entityName: string
  entityId: number
  clientId?: number | null
  beforeJson?: string | null
  afterJson?: string | null
  createdDate: string
}

export function getProductMeasureKindLabel(kind: ProductMeasureKind): string {
  switch (kind) {
    case ProductMeasureKind.Inventory:
      return 'Inventario'
    case ProductMeasureKind.Labor:
      return 'Mano de obra'
    case ProductMeasureKind.Service:
      return 'Servicio'
    case ProductMeasureKind.Yield:
      return 'Cosecha / venta'
    default:
      return 'Desconocido'
  }
}
